/**
 * Formattazione numerica italiana centralizzata.
 * Regole:
 *  - kg: virgola decimale, 3 decimali, NESSUN separatore delle migliaia
 *  - pz: intero, nessun decimale
 *  - €: virgola decimale, 2 decimali
 *  - %: virgola decimale, 0-1 decimali
 * Input parser: accetta indifferentemente "," o "." come separatore decimale.
 */

const nullish = (n) => n === null || n === undefined || Number.isNaN(Number(n));

/** "14,788" (3 decimali, virgola, no thousands sep) */
export function formatDecimal(n, dp = 3) {
  if (nullish(n)) return "—";
  const num = Number(n);
  const fixed = num.toFixed(dp);
  return fixed.replace(".", ",");
}

/** "14,788 kg" — 3 decimali fissi. `dp` override opzionale. */
export function formatKg(n, dp = 3) {
  if (nullish(n)) return "—";
  return `${formatDecimal(n, dp)} kg`;
}

/** "5 pz" — intero */
export function formatPieces(n) {
  if (nullish(n)) return "—";
  return `${Math.trunc(Number(n))} pz`;
}

/**
 * Parsa la pezzatura in kg a partire dalla stringa salvata in product.weight
 * (prodotta dall'helper backend `extract_pack_label_from_name`). Formati
 * attesi: "300 g", "1200 g", "4 kg". Litri NON convertiti (torna null).
 *
 * Decimale con virgola italiana (es "0,5 kg" → 0.5). Punto NON accettato
 * (coerente con il parser backend).
 *
 * Torna null quando: input vuoto, formato irriconoscibile, unità in litri
 * o altro. Il chiamante usa null per decidere di NON mostrare la stima.
 */
export function parsePackLabelKg(s) {
  // `nullish` è "forte" (NaN-aware) ed è pensato per numeri: su una stringa
  // non numerica come "300 g" tornerebbe true e saltiamo tutto. Qui `s` è
  // stringa, quindi check esplicito null/undefined/vuoto.
  if (s === null || s === undefined) return null;
  const str = String(s).trim();
  if (!str) return null;
  const m = str.match(/^(\d+(?:,\d+)?)\s*(kg|g)$/i);
  if (!m) return null;
  const n = Number(m[1].replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return m[2].toLowerCase() === "kg" ? n : n / 1000;
}

/**
 * Stima pezzi ricavata da giacenza kg / pezzatura kg. Visualizzazione:
 *   "≈ N pz" con N intero arrotondato;
 *   "≈ meno di 1 pz" quando l'arrotondamento darebbe 0 e la giacenza > 0.
 *
 * Il simbolo ≈ è sempre presente (è una stima, non un dato). Torna null
 * (quindi "non mostrare niente") se la stima non è calcolabile.
 */
export function formatEstimatedPieces(stockKg, packKg) {
  if (nullish(stockKg) || nullish(packKg)) return null;
  const sk = Number(stockKg);
  const pk = Number(packKg);
  if (!(sk > 0) || !(pk > 0)) return null;
  const n = Math.round(sk / pk);
  if (n === 0) return "≈ meno di 1 pz";
  // Intero all'italiana: nessun decimale, nessun separatore migliaia.
  return `≈ ${n} pz`;
}

/** Auto: kg se unit==='kg', altrimenti pz. */
export function formatQty(n, unit) {
  return unit === "kg" ? formatKg(n) : formatPieces(n);
}

/** "€ 15,40" (2 decimali) */
export function formatEur(n) {
  if (nullish(n)) return "—";
  return `€ ${Number(n).toFixed(2).replace(".", ",")}`;
}

/** "15,40 €/kg" */
export function formatEurPerKg(n) {
  if (nullish(n)) return "—";
  return `${Number(n).toFixed(2).replace(".", ",")} €/kg`;
}

/** "15,40 €/pz" */
export function formatEurPerPiece(n) {
  if (nullish(n)) return "—";
  return `${Number(n).toFixed(2).replace(".", ",")} €/pz`;
}

/** "14,3%" (1 decimale) o "14%" (dp=0) */
export function formatPct(n, dp = 1) {
  if (nullish(n)) return "—";
  return `${Number(n).toFixed(dp).replace(".", ",")}%`;
}

/**
 * Parser numerico: accetta stringhe con separatore decimale "," o ".", spazi,
 * unità ("kg", "g", "pz"). Ritorna il numero in kg se `asKg` è true, altrimenti
 * il numero grezzo.
 */
export function parseDecimal(input, { asKg = false } = {}) {
  if (input === null || input === undefined || input === "") return null;
  if (typeof input === "number") return input;
  const s = String(input).trim().toLowerCase().replace(",", ".");
  if (asKg) {
    const m = s.match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
    if (!m) return null;
    const num = parseFloat(m[1]);
    const unit = (m[2] || "kg").trim();
    if (Number.isNaN(num)) return null;
    if (unit === "g" || unit === "gr") return num / 1000;
    if (unit === "hg") return num / 10;
    return num;
  }
  const m = s.match(/^\s*(-?[0-9]*\.?[0-9]+)/);
  if (!m) return null;
  const num = parseFloat(m[1]);
  return Number.isNaN(num) ? null : num;
}

/** Stringa da mettere in un <input value=...> per un valore kg (senza suffisso). */
export function formatDecimalForInput(n, dp = 3) {
  if (nullish(n)) return "";
  return Number(n).toFixed(dp).replace(".", ",");
}
